/**
 * SpeedmasterWarranty.Cart.Summary.js
 * Cart warranty summary view — injected into cms:cart_summary_cms_area_1.
 *
 * Display-only panel:
 *   - Shows per-item warranty breakdown (which items have warranty opted)
 *   - Shows warranty total
 *   - Manages the single warranty service line (add/update/remove)
 *
 * CRITICAL: LiveOrderModel.fetch() re-renders the entire cart page, which
 * destroys this view and creates a new instance. All instance-level flags
 * are lost. Therefore sync guards MUST live at MODULE level to survive
 * view re-creation and prevent infinite add/remove loops.
 */
define('SpeedmasterWarranty.Cart.Summary', [
    'speedmaster_warranty_cart_summary.tpl',
    'SpeedmasterWarranty.Config',
    'LiveOrder.Model',
    'Backbone',
    'jQuery',
    'Utils'
], function SpeedmasterWarrantyCartSummaryDef(
    speedmaster_warranty_cart_summary_tpl,
    WarrantyConfig,
    LiveOrderModel,
    Backbone,
    jQuery,
    Utils
) {
    'use strict';

    /* ══════════════════════════════════════════════════════════════
     * MODULE-LEVEL GUARDS — survive view destroy/re-create cycles
     * ══════════════════════════════════════════════════════════════ */
    var _isSyncing = false;   // true while add/remove AJAX in flight
    var _syncTimer = null;    // debounce timer for toggle events

    /* ── UI lock — reuse existing .sm-cart-busy on cart summary ── */
    /* Same visual as SC's own loading state: pulse + pointer-events: none */
    function _lockUI() {
        var summary = document.querySelector('.cart-summary');
        if (summary) { summary.classList.add('sm-cart-busy'); }
    }
    function _unlockUI() {
        var summary = document.querySelector('.cart-summary');
        if (summary) { summary.classList.remove('sm-cart-busy'); }
    }

    return Backbone.View.extend({

        template: speedmaster_warranty_cart_summary_tpl,

        initialize: function initialize(options) {
            var self = this;
            self.cart = options.cart || null;
            self.cfg = WarrantyConfig.get();
            self.lines = [];
            self.hasWarrantyLine = false;
            self.warrantyLineId = null;
            self.currentWarrantyAmount = 0;
            self.optedTotal = 0;
            self.eligibleCount = 0;
            self.optedCount = 0;
            self.warrantyBreakdown = [];

            if (self.cart) {
                self._initialLoad();
                self._bindToggleEvent();
            }
        },

        /* ── data loading ────────────────────────────────────────── */

        /**
         * Initial load — fetch lines, compute, render.
         * If opted items exist and warranty line is missing/wrong, sync ONCE.
         * The module-level _isSyncing flag prevents re-entry across
         * view re-creations caused by LiveOrderModel.fetch().
         */
        _initialLoad: function _initialLoad() {
            var self = this;
            if (!self.cart) { return; }

            self.cart.getLines().then(function (lines) {
                self.lines = lines || [];
                self._computeBreakdown();
                self.render();

                // Only sync if not already syncing (module-level guard)
                if (!_isSyncing) {
                    self._syncWarrantyLine();
                }
            });
        },

        /**
         * Listen for per-item toggle events from Shopping.js.
         * Debounced to 300ms to batch rapid clicks.
         */
        _bindToggleEvent: function _bindToggleEvent() {
            var self = this;

            // Remove any previous listener (from destroyed view instance)
            jQuery(document).off('speedmasterWarranty:itemToggled.summaryView');

            jQuery(document).on('speedmasterWarranty:itemToggled.summaryView', function () {
                if (_isSyncing) { return; }

                // Debounce: user might click multiple toggles quickly
                clearTimeout(_syncTimer);
                _syncTimer = setTimeout(function () {
                    if (!self.cart || _isSyncing) { return; }

                    self.cart.getLines().then(function (lines) {
                        self.lines = lines || [];
                        self._computeBreakdown();
                        self.render();
                        self._syncWarrantyLine();
                    });
                }, 300);
            });
        },

        /* ── breakdown computation ───────────────────────────────── */

        _computeBreakdown: function _computeBreakdown() {
            var self = this;
            var cfg = self.cfg;
            var breakdown = [];
            var optedTotal = 0;
            var eligibleCount = 0;
            var optedCount = 0;

            // Get the per-item opt-in map from sessionStorage
            var itemMap = WarrantyConfig.getItemMap();

            self.hasWarrantyLine = false;
            self.warrantyLineId = null;
            self.currentWarrantyAmount = 0;

            for (var i = 0; i < self.lines.length; i++) {
                var line = self.lines[i];

                // Identify the warranty summary line itself
                if (line.item && line.item.internalid && line.item.internalid.toString() === cfg.warrantyItemId) {
                    self.hasWarrantyLine = true;
                    self.warrantyLineId = line.internalid;
                    self.currentWarrantyAmount = Number(line.amount) || 0;

                    // If warranty is the only line in cart, remove it
                    if (self.lines.length === 1 && !_isSyncing) {
                        _isSyncing = true;
                        _lockUI();
                        self._removeWarrantyLine(function () {
                            _isSyncing = false;
                            _unlockUI();
                        });
                    }
                    continue;
                }

                // Check eligibility
                if (!WarrantyConfig.isItemEligible(line.item)) {
                    continue;
                }

                var lineAmount = Number(line.amount) || 0;
                var warrantyCost = WarrantyConfig.calcLineWarrantyCost(lineAmount);
                var itemName = (line.item && line.item.storedisplayname2) ? line.item.storedisplayname2 :
                               (line.item && line.item.itemid) ? line.item.itemid : 'Item';
                var itemId = (line.item && line.item.internalid) ? line.item.internalid.toString() : '';
                var isOpted = !!(itemId && itemMap[itemId]);

                eligibleCount++;

                var lineQty = Number(line.quantity) || 1;

                breakdown.push({
                    lineId: line.internalid,
                    itemId: itemId,
                    itemName: itemName,
                    quantity: lineQty,
                    showQty: lineQty > 1,
                    lineAmount: lineAmount,
                    warrantyCost: warrantyCost,
                    warrantyCostFormatted: WarrantyConfig.formatPrice(warrantyCost),
                    isOpted: isOpted
                });

                if (isOpted) {
                    optedTotal += warrantyCost;
                    optedCount++;
                }
            }

            self.warrantyBreakdown = breakdown;
            self.optedTotal = Math.round(optedTotal * 100) / 100;
            self.eligibleCount = eligibleCount;
            self.optedCount = optedCount;
        },

        /* ── template context ────────────────────────────────────── */

        getContext: function getContext() {
            var self = this;
            var cfg = self.cfg;
            var currencySymbol = WarrantyConfig.getCurrencySymbol();

            return {
                showWarrantyOption:     self.eligibleCount > 0,
                hasOptedItems:          self.optedCount > 0,
                hasWarrantyLine:        self.hasWarrantyLine,
                termLabel:              cfg.termLabel,
                declineLabel:           cfg.declineLabel,
                optedTotalFormatted:    Utils.formatCurrency(self.optedTotal, currencySymbol),
                optedTotal:             self.optedTotal,
                breakdown:              self.warrantyBreakdown,
                eligibleCount:          self.eligibleCount,
                optedCount:             self.optedCount,
                isProcessing:           _isSyncing,
                warrantyItemId:         cfg.warrantyItemId
            };
        },

        /* ── warranty summary line sync ──────────────────────────── */

        /**
         * Sync the warranty service line to match opted items.
         *
         * Uses MODULE-LEVEL _isSyncing flag so it survives view re-creation.
         * After LiveOrderModel.fetch() re-renders the page, the new view
         * instance sees _isSyncing is still true and skips syncing.
         * When the AJAX callback finally runs, it sets _isSyncing = false.
         * But the callback's view reference is stale — so we DON'T call
         * any view methods from it. The page is already correct from the
         * fetch-triggered re-render.
         */
        _syncWarrantyLine: function _syncWarrantyLine() {
            var self = this;

            if (_isSyncing) { return; }

            var targetAmt = Math.round(self.optedTotal * 100) / 100;
            var currentAmt = Math.round((self.currentWarrantyAmount || 0) * 100) / 100;

            if (targetAmt > 0) {
                if (self.hasWarrantyLine && currentAmt === targetAmt) {
                    // Warranty line exists with correct amount — done
                    return;
                }
                if (self.hasWarrantyLine) {
                    // Amount differs — remove then add
                    _isSyncing = true;
                    _lockUI();
                    self._removeWarrantyLine(function () {
                        self._addWarrantyLine(function () {
                            _isSyncing = false;
                            _unlockUI();
                        });
                    });
                } else {
                    // No warranty line — add
                    _isSyncing = true;
                    _lockUI();
                    self._addWarrantyLine(function () {
                        _isSyncing = false;
                        _unlockUI();
                    });
                }
            } else if (targetAmt <= 0 && self.hasWarrantyLine) {
                // No opted items but warranty line exists — remove
                _isSyncing = true;
                _lockUI();
                self._removeWarrantyLine(function () {
                    _isSyncing = false;
                    _unlockUI();
                    // After removing, LiveOrderModel needs to be told
                    LiveOrderModel.getInstance().fetch();
                });
            }
        },

        /**
         * Add warranty service item via additemtocart.nl.
         * @param {Function} [callback] Called after LiveOrderModel.fetch()
         */
        _addWarrantyLine: function _addWarrantyLine(callback) {
            var self = this;
            var cfg = self.cfg;

            if (self.optedTotal <= 0) {
                if (typeof callback === 'function') { callback(); }
                return;
            }

            self.render(); // show "Updating warranty..."

            var siteid = (typeof SC !== 'undefined' && SC.ENVIRONMENT && SC.ENVIRONMENT.siteSettings) ?
                          SC.ENVIRONMENT.siteSettings.siteid : '';

            jQuery.ajax({
                type: 'POST',
                url: window.location.protocol + '//' + window.location.host + '/app/site/backend/additemtocart.nl',
                data: {
                    n:        siteid,
                    buyid:    cfg.warrantyItemId,
                    qty:      1,
                    showcart: 'F',
                    amount:   self.optedTotal.toFixed(2)
                }
            }).done(function () {
                // fetch() re-renders the page (destroys this view, creates new one).
                // The new view's _initialLoad will see the warranty line and
                // _isSyncing = true, so it won't try to sync again.
                LiveOrderModel.getInstance().fetch().done(function () {
                    if (typeof callback === 'function') { callback(); }
                }).fail(function () {
                    if (typeof callback === 'function') { callback(); }
                });
            }).fail(function () {
                _isSyncing = false;
                _unlockUI();
                if (typeof callback === 'function') { callback(); }
            });
        },

        /**
         * Remove the warranty service line from the cart.
         * @param {Function} [callback] Called when complete
         */
        _removeWarrantyLine: function _removeWarrantyLine(callback) {
            var self = this;
            if (!self.cart || !self.hasWarrantyLine || !self.warrantyLineId) {
                if (typeof callback === 'function') { callback(); }
                return;
            }

            self.cart.removeLine({
                line_id: self.warrantyLineId
            }).then(function () {
                self.hasWarrantyLine = false;
                self.warrantyLineId = null;
                self.currentWarrantyAmount = 0;
                if (typeof callback === 'function') { callback(); }
            }, function () {
                self.hasWarrantyLine = false;
                self.warrantyLineId = null;
                self.currentWarrantyAmount = 0;
                if (typeof callback === 'function') { callback(); }
            });
        },

        /**
         * Cleanup on view destroy.
         */
        destroy: function destroy() {
            jQuery(document).off('speedmasterWarranty:itemToggled.summaryView');
            clearTimeout(_syncTimer);
            Backbone.View.prototype.destroy.apply(this, arguments);
        }

    });
});
